import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  computed,
  inject,
  signal
} from '@angular/core';
import {CommonModule, TitleCasePipe} from '@angular/common';
import {MaterialColorGridComponent} from '@app/model-composer/components/material-color-grid.component';
import {GroupingTabsComponent} from '@app/model-composer/components/customization-grouping-tabs.component';
import {MacroColorSelectorComponent} from '@app/model-composer/components/customization-macro-color-select.component';
import {MaterialSelectorComponent} from '@app/model-composer/components/customization-material-selector.component';
import {CompositionCustomizationService} from '@app/model-composer/services/composition-customization.service';
import {RowSelection} from '@app/model-composer/models/customization.model';

@Component({
  selector: 'app-customization-options',
  standalone: true,
  imports: [
    CommonModule,
    TitleCasePipe,
    GroupingTabsComponent,
    MacroColorSelectorComponent,
    MaterialSelectorComponent,
    MaterialColorGridComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="ipe:space-y-6 ipe:flex ipe:flex-col">
      <div class="ipe:space-y-2">
        @let active = activeOptionId();

        @for (option of options; track option.id) {
          @let isActive = option.id === active;

          <button
            type="button"
            class="
              ipe:cursor-pointer
              ipe:w-full ipe:py-4 ipe:px-6
              ipe:text-left ipe:text-sm ipe:tracking-widest ipe:font-medium
              ipe:border ipe:rounded-sm ipe:border-gray-200
              ipe:transition-all ipe:duration-300
            "
            [ngClass]="isActive
              ? 'ipe:bg-[#F9F8F6] ipe:text-black ipe:text-sm ipe:border-[#F9F8F6] ipe:shadow-lg'
              : 'ipe:bg-white ipe:text-gray-400 ipe:hover:text-gray-600 ipe:hover:border-white'
            "
            (click)="selectOption(option)"
          >
            {{ option.label | titlecase }}
          </button>
        }
      </div>

      @if (activeOption(); as opt) {
        @if (groupingMode() === 'tabs') {
          <app-grouping-tabs
            [active]="activeTab()"
            (change)="setActiveTab($event)"
          />
        }

        @if (groupingMode() === 'macro' || activeTab() === 'macro') {
          <app-macro-color-selector
            [items]="macroColors()"
            [activeId]="activeMacroColorId()"
            (select)="activeMacroColorId.set($event)"
          />
        }

        @if (groupingMode() === 'material' || activeTab() === 'material') {
          <app-material-selector
            [items]="materials()"
            [activeId]="activeMaterialId()"
            (select)="activeMaterialId.set($event)"
          />
        }

        @if (canShowGrid()) {
          <div class="ipe:flex ipe:items-center ipe:gap-4 ipe:mb-6 ipe:mt-2">
            <div class="ipe:h-[1px] ipe:bg-gray-100 ipe:flex-1"></div>
            <span class="ipe:text-[11px] ipe:tracking-[0.2em] ipe:text-gray-400 ipe:font-medium">
              Varianti disponibili
            </span>
            <div class="ipe:h-[1px] ipe:bg-gray-100 ipe:flex-1"></div>
          </div>

          <app-material-color-grid
            [items]="filteredItems()"
            [selected]="selected"
            [hideEtoToggle]="shouldHideGridEtoToggle()"
            (select)="onMaterialColorSelect($event)"
          />
        }
      }
    </div>
  `,
  styles: [
    `
      @import 'tailwindcss' prefix(ipe);
      @theme {
        --font-display: 'ABC Walter Neue', sans-serif;
        --font-roboto: 'ABC Walter Neue', sans-serif;
        --font-sans: 'ABC Walter Neue', sans-serif;
      }
    `
  ]
})
export class CustomizationOptionsComponent implements OnChanges {
  @Input({ required: true }) options!: any[];
  @Input() selected?: RowSelection;

  @Output() selectMaterialColor = new EventEmitter<{
    optionId: string;
    eligiblePlacements: string[];
    placementsToRemove?: string[];
    placementsToRestore?: string[];
    materialColor: { materialId: number; colorId: number };
    item: any;
  }>();

  private _tabPinnedByUser = false;
  private service = inject(CompositionCustomizationService);

  private _activeOptionId = signal<string | null>(null);
  readonly activeOptionId = this._activeOptionId.asReadonly();

  readonly activeTab = signal<'macro' | 'material'>('material');
  readonly activeMacroColorId = signal<string | null>(null);
  readonly activeMaterialId = signal<number | null>(null);

  readonly activeOption = computed(() => this.options.find(o => o.id === this.activeOptionId()));

  readonly groupingMode = computed(() => {
    const opt = this.activeOption();

    if (!opt) {
      return 'none';
    }

    if (opt.macroColorGrouping && opt.materialGrouping) {
      return 'tabs';
    }

    if (opt.materialGrouping) {
      return 'material';
    }

    if (opt.macroColorGrouping) {
      return 'macro';
    }

    return 'none';
  });

  readonly canShowGrid = computed(() => {
    const opt = this.activeOption();

    if (!opt) {
      return false;
    }

    if (!opt.macroColorGrouping && !opt.materialGrouping) {
      return true;
    }

    if (opt.macroColorGrouping && !opt.materialGrouping) {
      return this.activeMacroColorId() !== null;
    }

    if (!opt.macroColorGrouping && opt.materialGrouping) {
      return this.activeMaterialId() !== null;
    }

    if (opt.macroColorGrouping && opt.materialGrouping) {
      return this.activeTab() === 'macro'
        ? this.activeMacroColorId() !== null
        : this.activeMaterialId() !== null;
    }

    return false;
  });

  readonly normalized = computed(() => this.service.normalize(this.activeOption()));
  readonly macroColors = computed(() => this.normalized().macroColors);
  readonly materials = computed(() => this.normalized().materials);

  readonly filteredItems = computed(() => {
    const opt = this.activeOption();

    if (!opt) {
      return [];
    }

    const mode = this.groupingMode();
    const normalized = this.normalized();

    switch (mode) {
      case 'none':
        return normalized.items;
      case 'macro':
        return this.macroItems(normalized);
      case 'material':
        return this.materialItems(normalized);
      case 'tabs':
        return this.activeTab() === 'macro'
          ? this.macroItems(normalized)
          : this.materialItems(normalized);
      default:
        return [];
    }
  });

  readonly activeMaterialGroup = computed(() => {
    const materialId = this.activeMaterialId();

    if (materialId == null) {
      return null;
    }

    return this.materials().find((material: any) => material.id === materialId) ?? null;
  });

  readonly shouldHideGridEtoToggle = computed(() => {
    const mode = this.groupingMode();

    if (mode !== 'material' && !(mode === 'tabs' && this.activeTab() === 'material')) {
      return false;
    }

    return this.activeMaterialGroup()?.eto === true;
  });

  private materialItems(normalized = this.normalized()): any[] {
    return this.activeMaterialId()
      ? normalized.materials.find((m: any) => m.id === this.activeMaterialId())?.items ?? []
      : [];
  }

  private macroItems(normalized = this.normalized()): any[] {
    return this.activeMacroColorId()
      ? normalized.macroColors.find((m: any) => m.id === this.activeMacroColorId())?.items ?? []
      : [];
  }

  selectOption(option: any): void {
    this._activeOptionId.set(option.id);
    this._tabPinnedByUser = false;
    this.activeMacroColorId.set(null);
    this.activeMaterialId.set(null);
    this.activeTab.set('material');
  }

  setActiveTab(tab: 'macro' | 'material'): void {
    this._tabPinnedByUser = true;
    this.activeTab.set(tab);

    if (tab === 'macro') {
      this.activeMaterialId.set(null);
    }

    if (tab === 'material') {
      this.activeMacroColorId.set(null);
    }
  }

  onMaterialColorSelect(item: any): void {
    const opt = this.activeOption();

    if (!opt) {
      return;
    }

    this.selectMaterialColor.emit({
      optionId: opt.id,
      materialColor: {
        materialId: item.material.materialId,
        colorId: item.color.colorId
      },
      item,
      eligiblePlacements: opt?.eligiblePlacements ?? opt?.eligible_placements ?? [],
      placementsToRestore: opt?.placementToRestore,
      placementsToRemove: opt?.placementToRemove
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selected'] || changes['options']) {
      this.preselectFromSelected();
    }

    if (!this.selected && this.activeOptionId() === null && this.options?.length) {
      this._activeOptionId.set(this.options[0].id);
    }
  }

  private preselectFromSelected(force = false): void {
    const sel = this.selected;

    if (!sel || !this.options?.length) {
      return;
    }

    const optionId = this.findOptionIdForSelection(sel);

    if (!optionId) {
      return;
    }

    const optionChanged = this.activeOptionId() !== optionId;

    if (optionChanged) {
      this._tabPinnedByUser = false;
    }

    this._activeOptionId.set(optionId);

    const opt = this.options.find(o => o.id === optionId);

    if (!opt) {
      return;
    }

    const normalized = this.service.normalize(opt);

    const match = (item: any) =>
      item?.material?.materialId === sel.materialColor?.materialId &&
      item?.color?.colorId === sel.materialColor?.colorId;

    const macroGroup = opt.macroColorGrouping
      ? (normalized.macroColors ?? []).find((g: any) => (g.items ?? []).some(match))
      : null;

    const matGroup = opt.materialGrouping
      ? (normalized.materials ?? []).find((m: any) => (m.items ?? []).some(match))
      : null;

    const foundMacro = !!macroGroup?.id;
    const foundMaterial = matGroup?.id != null;

    if (foundMacro) {
      this.activeMacroColorId.set(macroGroup.id);
    }

    if (foundMaterial) {
      this.activeMaterialId.set(matGroup.id);
    }

    if (opt.macroColorGrouping && opt.materialGrouping) {
      if (this._tabPinnedByUser) {
        if (this.activeTab() === 'material' && !foundMaterial && foundMacro) {
          this.activeTab.set('macro');
        }

        if (this.activeTab() === 'macro' && !foundMacro && foundMaterial) {
          this.activeTab.set('material');
        }
      } else {
        if (foundMaterial && !foundMacro) {
          this.activeTab.set('material');
        } else {
          this.activeTab.set('material');
        }
      }
    } else {
      if (opt.materialGrouping && !opt.macroColorGrouping) {
        this.activeTab.set('material');
      }

      if (opt.macroColorGrouping && !opt.materialGrouping) {
        this.activeTab.set('macro');
      }
    }
  }

  private findOptionIdForSelection(sel: RowSelection): string | null {
    const matId = sel.materialColor?.materialId;
    const colId = sel.materialColor?.colorId;

    if (matId == null || colId == null) {
      return null;
    }

    const opt = this.options.find((o: any) =>
      (o.materialColorList ?? []).some((it: any) =>
        it?.material?.materialId === matId && it?.color?.colorId === colId
      )
    );

    return opt?.id ?? null;
  }
}
